import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useBrandMode } from "./BrandModeContext";
import { fetchDeal, startThread, submissionAction } from "./services/brandModeServices";
import { EMPTY_VALUE, fmtMoney, fullName } from "./brandUtils";
import { SUBMISSION_STATUS, complianceChecks, fmtDate, fmtDateRange, timeAgo } from "./brandDealUtils";
import {
  ActionBar,
  BackLink,
  Badge,
  BmModal,
  Btn,
  CampaignContextBar,
  Card,
  DealStepper,
  ErrorState,
  Field,
  Loading,
  Overline,
  TextArea,
} from "./ui/BrandUI";

const STEP_FOR_STATUS = { draft: 1, open: 1, in_contract: 2, in_progress: 3, completed: 4 };

const ContentApprovalPage = () => {
  const navigate = useNavigate();
  const { dealId, submissionId } = useParams();
  const { refreshBadges } = useBrandMode();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const payload = await fetchDeal(dealId);
      setData(payload);
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load the submission");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, submissionId]);

  const found = useMemo(() => {
    const deal = data?.deal;
    if (!deal) return null;
    for (const d of deal.deliverables || []) {
      const idx = (d.submissions || []).findIndex((s) => s.id === submissionId);
      if (idx >= 0) return { deliverable: d, submission: d.submissions[idx], index: idx + 1 };
    }
    return null;
  }, [data, submissionId]);

  const act = async (action, payload) => {
    setBusy(action);
    try {
      await submissionAction(dealId, submissionId, action, payload);
      toast.success(action === "approve" ? "Content approved." : "Revision requested.");
      refreshBadges();
      navigate(`/brand/deals/${dealId}`);
    } catch (err) {
      toast.error(err.message || "Could not update the submission");
    } finally {
      setBusy(null);
    }
  };

  const message = async () => {
    try {
      const thread = await startThread(data.deal.campaignId, data.deal.athleteId);
      navigate(`/brand/campaigns/${data.deal.campaignId}/messages?thread=${thread.id}`);
    } catch (err) {
      toast.error(err.message || "Could not open the conversation");
    }
  };

  if (error) return <div className="py-10"><ErrorState>{error}</ErrorState></div>;
  if (!data) return <Loading label="Loading submission..." />;

  const { deal, campaign, brand } = data;
  const athleteName = fullName(deal.athlete);

  if (!found) {
    return (
      <div className="flex flex-col gap-5 pt-6">
        <BackLink to={`/brand/deals/${dealId}`}>Back to deal</BackLink>
        <Card className="py-12 text-center bm-body-m text-bm-muted">That submission is no longer on this deal.</Card>
      </div>
    );
  }

  const { deliverable, submission, index } = found;
  const unit = `${(deliverable.contentType || "post").toLowerCase()} ${index} of ${deliverable.quantity || 1}`;
  const pending = (deal.milestones || []).find((m) => m.status === "pending_approval");
  const nextMilestone = pending || (deal.milestones || []).find((m) => m.status === "scheduled");
  const status = SUBMISSION_STATUS[submission.status] || SUBMISSION_STATUS.under_review;
  const reviewable = submission.status === "under_review";
  const checks = complianceChecks({ submission, deliverable, campaign, brandName: brand?.company });

  return (
    <div className="flex flex-col gap-6 pt-6">
      <BackLink to={`/brand/campaigns/${deal.campaignId}`}>Back to campaign</BackLink>
      <CampaignContextBar
        campaign={campaign}
        linkTo={`/brand/campaigns/${deal.campaignId}`}
        right={
          <span>
            {[fmtDateRange(campaign?.startDate, campaign?.endDate), campaign?.budget?.total ? `${fmtMoney(campaign.budget.total)} budget` : null, `${campaign?.athleteIds?.length || 0} athletes`]
              .filter(Boolean)
              .join("  ·  ")}
          </span>
        }
      />
      <DealStepper current={STEP_FOR_STATUS[campaign?.status] || 3} />

      <div className="flex flex-col gap-[6px]">
        <Overline brand>Deal flow</Overline>
        <h1 className="bm-h-l text-bm-ink">Content Approval</h1>
        <p className="bm-body-l text-bm-muted">
          {athleteName} submitted {unit} for review
        </p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <Card className="flex min-w-0 flex-1 flex-col gap-5">
          <div className="flex items-center justify-between gap-3">
            <span className="flex flex-col gap-[2px]">
              <span className="bm-h-s text-bm-ink">{athleteName}</span>
              <span className="bm-body-s text-bm-muted">
                {[deal.athlete?.position, deal.athlete?.school, `Submitted ${timeAgo(submission.submittedAt)}`].filter(Boolean).join(" · ")}
              </span>
            </span>
            <Badge tone={status.tone} dot={status.dot}>{status.label}</Badge>
          </div>

          <div className="overflow-hidden rounded-[8px] border border-bm-border">
            {submission.imageUrl ? (
              <img src={submission.imageUrl} alt="" className="max-h-[560px] w-full object-cover" />
            ) : (
              <div className="flex aspect-[4/5] w-full flex-col items-center justify-center gap-2 bg-bm-mutedbg bm-body-m text-bm-muted">
                No screenshot attached
                {submission.link && (
                  <a href={submission.link} target="_blank" rel="noreferrer" className="bm-label-l text-bm-orange underline">
                    Open the post
                  </a>
                )}
              </div>
            )}
            <div className="flex flex-col gap-3 bg-bm-subtle p-4">
              <p className="bm-body-m text-bm-ink">{submission.caption || "No caption provided."}</p>
              <div className="flex flex-wrap gap-2">
                <Badge tone="outline">{`${deliverable.platform || ""} ${unit}`.trim()}</Badge>
                {deliverable.notes && <Badge tone="outline">{deliverable.notes}</Badge>}
                {(submission.tags || []).map((t) => (
                  <Badge key={t} tone="outline">{t}</Badge>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-bm-mutedbg pt-3">
            <span className="bm-label-xs text-bm-ink">
              {submission.imageUrl ? "Screenshot attached" : submission.link ? "Link attached" : "No proof attached"}
              {submission.link && submission.imageUrl && (
                <>
                  {" · "}
                  <a href={submission.link} target="_blank" rel="noreferrer" className="text-bm-orange underline">view post</a>
                </>
              )}
            </span>
            <span className="bm-body-s text-bm-muted">{submission.postedAt ? `Posted on ${fmtDate(submission.postedAt)}` : "Post date not given"}</span>
          </div>
          {submission.status === "revision_requested" && submission.revisionNote && (
            <div className="rounded-[8px] bg-bm-subtle px-4 py-3 bm-body-m text-bm-muted">Revision requested: {submission.revisionNote}</div>
          )}
        </Card>

        <div className="flex w-full flex-col gap-5 lg:w-[380px]">
          <Card className="flex flex-col gap-4">
            <Overline>Deal summary</Overline>
            <div className="flex gap-3">
              <div className="flex flex-1 flex-col gap-1 rounded-[8px] bg-bm-subtle p-3">
                <Overline>Total contract</Overline>
                <span className="font-inter text-[24px] font-extrabold text-bm-ink">{fmtMoney(deal.offer?.compensation)}</span>
              </div>
              <div className="flex flex-1 flex-col gap-1 rounded-[8px] bg-bm-subtle p-3">
                <Overline>Current milestone</Overline>
                <span className="font-inter text-[24px] font-extrabold text-bm-orange">{nextMilestone ? fmtMoney(nextMilestone.amount) : EMPTY_VALUE}</span>
              </div>
            </div>
            <p className="bm-body-s text-bm-muted">
              {pending
                ? `This is ${unit}. Approving releases the ${fmtMoney(pending.amount)} milestone payment.`
                : nextMilestone
                  ? `This is ${unit}. The next ${fmtMoney(nextMilestone.amount)} milestone unlocks once its deliverables are approved.`
                  : "Every milestone on this deal has been released."}
            </p>
          </Card>

          <Card className="flex flex-col gap-4">
            <Overline>Deliverables status</Overline>
            <div className="flex flex-col gap-3">
              {(deal.deliverableStatusRows || []).map((row, i) => {
                const meta = SUBMISSION_STATUS[row.status] || SUBMISSION_STATUS.not_posted;
                const current = row.submissionId === submissionId;
                return (
                  <div key={`${row.deliverableId}-${i}`} className="flex items-center justify-between gap-3">
                    <span className={current || row.status !== "not_posted" ? "bm-label-l text-bm-ink" : "bm-body-m text-bm-muted"}>{row.label}</span>
                    <Badge tone={meta.tone} dot={meta.dot}>{meta.label}</Badge>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="flex flex-col gap-4">
            <Overline>Guidelines compliance</Overline>
            <div className="flex flex-col gap-2">
              {checks.map((c) => (
                <div key={c.label} className="flex items-start gap-2 bm-body-m">
                  <span className={`mt-[2px] flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${c.ok === true ? "bg-bm-positive/10 text-bm-positive" : c.ok === false ? "bg-bm-critical/10 text-bm-critical" : "bg-bm-mutedbg text-bm-muted"}`}>
                    {c.ok === true ? "✓" : c.ok === false ? "!" : "•"}
                  </span>
                  <span className={c.ok === false ? "text-bm-ink" : "text-bm-muted"}>{c.label}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <ActionBar
        left={
          <>
            <Btn onClick={() => act("approve", { releaseMilestone: true })} disabled={!reviewable || !!busy}>
              {busy === "approve" ? "Approving..." : "Approve content"}
            </Btn>
            <Btn variant="secondary" onClick={() => setRevisionOpen(true)} disabled={!reviewable || !!busy}>
              Request revision
            </Btn>
          </>
        }
        right={
          <button type="button" onClick={message} className="bm-label-l text-bm-muted hover:text-bm-ink">
            Message athlete
          </button>
        }
      />

      {revisionOpen && (
        <BmModal
          title="Request a revision"
          onClose={() => setRevisionOpen(false)}
          footer={
            <>
              <Btn variant="secondary" className="flex-1" onClick={() => setRevisionOpen(false)}>Cancel</Btn>
              <Btn className="flex-1" disabled={busy === "revision"} onClick={() => act("revision", { note })}>
                {busy === "revision" ? "Sending..." : "Send request"}
              </Btn>
            </>
          }
        >
          <Field label="What should change?" hint="The athlete gets this note in-app and by email.">
            <TextArea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Please show the product in the first frame and add #ad." />
          </Field>
        </BmModal>
      )}
    </div>
  );
};

export default ContentApprovalPage;
